import React, {FC, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {AutoComplete, Colors, Text} from '@optimaxdev/design-system';

import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {
    NoResultComponent,
    Suggestion,
} from 'components/success/contacts/smartDoctorSuggestions/smartDoctorSuggestions';
import {TYP_CONTACT_LENSES, TYP_TYP_FORM_ACTION} from 'constants/analytics/events/typ';
import {DOCTOR_DETAILS_DEFAULT} from 'constants/product';
import {CL_RX_METHOD} from 'constants/success';
import {ApplicationStoreType} from 'desktop/store';
import {useGetAlgoliaDoctorsDetails} from 'hooks/success/useGetAlgoliaDoctorsDetails/useGetAlgoliaDoctorsDetails';
import {getLabel} from 'libs/success/helpers/helpers';
import {getPopupType} from 'libs/success/prescriptionPopup/prescriptionPopup';
import {
    DoctorDetailsType,
    MANUALLY_FILLING_METHOD,
    SUCCESS_PAGE_FIELDS_DEFAULT,
} from 'types/successPage';

import {getDrawerState} from '../../../selectorsV2';
import {changeField} from '../../../store/actions/orderItems/orderItems';
import {setDrawerMethod} from '../../../store/reducers/drawerPopup/drawerPopup';
import {getDoctorDetails, getValidatedItems} from '../../../store/selectors/orderItems/orderItems';

export type PropsType = {
    resetError: (key: string) => void;
};

/**
 * Wrapper for autocomplete for d typ
 */
export const AutoCompleteWrapper: FC<PropsType> = ({resetError}) => {
    const [autoCompleteValue, setAutoCompleteValue] = useState<string>('');
    const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);

    const {doctorDetails, item, validatedItems} = useSelector(
        (store: ApplicationStoreType) => ({
            doctorDetails: getDoctorDetails(store),
            item: getDrawerState(store).item,
            validatedItems: getValidatedItems(store),
        }),
        shallowEqual,
    );
    const dispatch = useDispatch();
    const parsedItem = validatedItems?.[item?.id || ''] as DoctorDetailsType;

    const {suggestions} = useGetAlgoliaDoctorsDetails(autoCompleteValue);
    const selectedSuggestion = suggestions?.find(({id}) => id === selectedSuggestionId);

    const selectHandler = (suggestionId: string | null) => {
        setSelectedSuggestionId(suggestionId);
        const selected = suggestions?.find(({id}) => id === suggestionId);

        const CHANGE_FIELD_BASE_PROPS = {
            ...SUCCESS_PAGE_FIELDS_DEFAULT,
            isAutocompleteModeEnabled: true,
            orderItemId: String(item?.id),
            sendBy: item?.prescription?.send_by || 0,
        };
        resetError('firstName');
        sendDesktopAnalyticsEvent(TYP_TYP_FORM_ACTION, {
            label: suggestionId ? 'Select' : 'Remove',
            version: 'D',
        });

        const DOCTOR_DETAILS = {
            ...DOCTOR_DETAILS_DEFAULT,
            patientName: doctorDetails?.patientName || '',
            ...(selected && {
                ...selected,
                city: selected.mailingAddressCity,
                name: `${selected.firstName} ${selected.lastName}`,
                regionCode: selected.mailingAddressState,
            }),
        };

        dispatch(
            changeField({
                ...CHANGE_FIELD_BASE_PROPS,
                doctorDetails: DOCTOR_DETAILS,
            }),
        );
    };

    const handleCantFindDoctor = () => {
        const type = getPopupType(item, '');
        dispatch(
            changeField({
                ...SUCCESS_PAGE_FIELDS_DEFAULT,
                doctorDetails: {
                    ...DOCTOR_DETAILS_DEFAULT,
                    filling_method: MANUALLY_FILLING_METHOD,
                    patientName: doctorDetails?.patientName || '',
                },
                isAutocompleteModeEnabled: false,
                orderItemId: String(item?.id),
                sendBy: item?.prescription?.send_by || 0,
            }),
        );
        dispatch(setDrawerMethod({rxMethod: CL_RX_METHOD.MANUALLY_DOCTOR}));
        sendDesktopAnalyticsEvent(
            TYP_CONTACT_LENSES,
            `${getLabel(CL_RX_METHOD.DOCTOR, false, type)} - Can't find your doctor`,
        );
    };

    return (
        <>
            <Text as="p" color={Colors.nevada} lineHeight="150%" margin="20px 0 12px" size="14px">
                Find your eye doctor or clinic:
            </Text>
            <AutoComplete
                inputOptions={{
                    isError: Boolean(parsedItem?.firstName),
                    label: 'Doctor, clinic, ZIP code, or address',
                    message: parsedItem?.firstName,
                    onInput: () => resetError('firstName'),
                    size: 'large',
                }}
                selectedSuggestion={
                    selectedSuggestion && <Suggestion {...selectedSuggestion} isSelected />
                }
                suggestions={suggestions.map(suggestion => ({
                    ...suggestion,
                    inputValue: autoCompleteValue,
                }))}
                inputOnChange={setAutoCompleteValue}
                isApproved={Boolean(selectedSuggestion)}
                noResultClickHandler={handleCantFindDoctor}
                noResultComponent={NoResultComponent}
                onSelect={selectHandler}
                stickyFooterClickHandler={handleCantFindDoctor}
                stickyFooterComponent={NoResultComponent}
                suggestionComponent={Suggestion}
                valueLengthToOpenResults={2}
            />
        </>
    );
};
